/** One section of the tour operator's Defaults tab: a heading row (title, a link to the
 *  section's own URL, and the edit bar), a description, then the section's body.
 *
 *  The edit bar mirrors `EditorSaveBar` — status in words, Discard, "Save N changes" with
 *  its disabled reason, ⌘S — but sits in the heading row, so entering edit mode moves
 *  nothing (Zero-Re-Layout): Edit and the Discard / Save pair share one slot, and the
 *  status line is reserved in both modes. While a section is edited its heading row sticks
 *  to the top of the page, so Save stays in reach under a long table. The navigation guard
 *  is the tab's (one blocker per page), not the bar's.
 *
 *  The button pressed leaves the tree with the mode, so the bar hands focus over: Edit →
 *  Discard (Done), and Discard / Done / a save → Edit — the same in both sections. */
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { operatorDefaultsHref, type OperatorDefaultsSection } from '../tenantLinks';

export type DefaultsSectionId = OperatorDefaultsSection;

export const DEFAULTS_SECTIONS: readonly DefaultsSectionId[] = ['tag-categories', 'trip-settings'];

export const sectionDomId = (id: DefaultsSectionId) => `top-defaults-${id}`;

export interface SectionEditState {
  canManage: boolean;
  editing: boolean;
  /** Why Edit is unavailable right now (the other section is being edited). */
  editBlockedReason?: string | null;
  /** Changes in the draft (0: clean). */
  count: number;
  saving: boolean;
  /** Why the draft cannot be saved (a rule the edit broke) — disables Save, is the status. */
  blockReason?: string | null;
  /** The last failed save, in words (the status, until the next attempt). */
  error?: string | null;
  /** The save dialog (or a confirm over it) is open: it has the keyboard, so ⌘S neither
   *  reopens it — which would reset what is typed in it — nor saves the page. */
  dialogOpen?: boolean;
  onEdit: () => void;
  /** Leaves edit mode, dropping the draft. */
  onDiscard: () => void;
  /** Opens the save dialog. */
  onSave: () => void;
}

export function DefaultsSection({
  id,
  topId,
  title,
  description,
  edit,
  children,
}: {
  id: DefaultsSectionId;
  topId: number;
  title: string;
  description: ReactNode;
  edit: SectionEditState;
  children: ReactNode;
}) {
  const titleId = useId();
  return (
    <Box
      component="section"
      id={sectionDomId(id)}
      aria-labelledby={titleId}
      data-defaults-section={id}
      sx={{ display: 'grid', gap: 1, minWidth: 0, scrollMarginTop: 16 }}
    >
      <Box
        data-section-heading
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 44,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          // Sticky changes no geometry, so switching it with the mode moves nothing. Above the
          // associations table's sticky header cells (z-index 4), which scroll under it.
          ...(edit.editing ? { position: 'sticky', top: 0, zIndex: 5 } : {}),
        }}
      >
        <Typography
          id={titleId}
          component="h2"
          variant="subtitle1"
          sx={{ fontWeight: 600, flex: 'none' }}
        >
          {title}
        </Typography>
        {/* Beside the heading, not in it: inside, its name would join the heading's. */}
        <Tooltip title={`Link to ${title.toLowerCase()}`}>
          <IconButton
            component={RouterLink}
            to={operatorDefaultsHref(topId, id)}
            size="small"
            sx={{
              flex: 'none',
              p: 0.25,
              ml: -0.5,
              color: 'text.disabled',
              '&:hover': { color: 'text.secondary' },
            }}
          >
            <LinkIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: '1 0 0' }} />
        <EditBar edit={edit} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        {description}
      </Typography>
      {children}
    </Box>
  );
}

function EditBar({ edit }: { edit: SectionEditState }) {
  const { canManage, editing, count, saving, blockReason, error } = edit;
  const dirty = count > 0;
  const saveDisabledReason = !dirty ? 'No changes to save' : (blockReason ?? null);

  // ⌘S / Ctrl+S opens the save dialog — only while this section is edited and saveable
  // (one section is edited at a time, so one listener at most), and not again while the
  // dialog is open.
  const { onSave, dialogOpen = false } = edit;
  useEffect(() => {
    if (!editing || !dirty || saving || blockReason) return undefined;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (!dialogOpen) onSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editing, dirty, saving, blockReason, dialogOpen, onSave]);

  // Switching mode swaps the buttons: the one just pressed (or the save dialog's) is gone,
  // and focus would drop to the page. It goes to the other mode's first button — unless the
  // keyboard has meanwhile moved on to something else on the page.
  const barRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLButtonElement>(null);
  const discardRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(editing);
  useEffect(() => {
    if (wasEditing.current === editing) return;
    wasEditing.current = editing;
    const active = document.activeElement;
    const elsewhere =
      active instanceof HTMLElement &&
      active !== document.body &&
      active.isConnected &&
      !barRef.current?.contains(active) &&
      !active.closest('[role="dialog"]');
    if (!elsewhere) (editing ? discardRef : editRef).current?.focus();
  }, [editing]);

  let status: ReactNode = null;
  if (!canManage) {
    status = (
      <StatusText
        icon={<LockOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />}
        color="text.secondary"
      >
        Read-only — you lack the manage permission
      </StatusText>
    );
  } else if (editing) {
    status = saving ? (
      <StatusText color="text.secondary">Saving…</StatusText>
    ) : error ? (
      <StatusText icon={<ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />} color="error.main">
        {error}
      </StatusText>
    ) : dirty && blockReason ? (
      <StatusText icon={<ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />} color="error.main">
        {blockReason}
      </StatusText>
    ) : dirty ? (
      <StatusText
        icon={<EditNoteIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main' }} />}
        color="warning.dark"
      >
        {count} unsaved {count === 1 ? 'change' : 'changes'}
      </StatusText>
    ) : (
      <StatusText color="text.secondary">No changes yet</StatusText>
    );
  }

  return (
    // One line in both modes: a long status ellipsizes (its words in the tooltip and the
    // live region) rather than wrap the heading row and move the section under it.
    <Box
      ref={barRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flex: '0 1 auto',
        minWidth: 0,
        '& > :not([data-edit-status])': { flexShrink: 0 },
      }}
    >
      <Box aria-live="polite" sx={{ minWidth: 0, overflow: 'hidden' }} data-edit-status>
        {status}
      </Box>
      {canManage &&
        (editing ? (
          <>
            <Button
              ref={discardRef}
              size="small"
              onClick={edit.onDiscard}
              disabled={saving}
              sx={{ minWidth: 84 }}
              data-discard
            >
              {dirty ? 'Discard' : 'Done'}
            </Button>
            <Tooltip title={saveDisabledReason ?? ''}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  onClick={edit.onSave}
                  disabled={saveDisabledReason != null || saving}
                  data-save
                >
                  {dirty ? `Save ${count} ${count === 1 ? 'change' : 'changes'}` : 'Save changes'}
                </Button>
              </span>
            </Tooltip>
          </>
        ) : (
          <Tooltip title={edit.editBlockedReason ?? ''}>
            <span>
              <Button
                ref={editRef}
                size="small"
                variant="contained"
                startIcon={<EditOutlinedIcon />}
                onClick={edit.onEdit}
                disabled={Boolean(edit.editBlockedReason)}
                data-edit
              >
                Edit
              </Button>
            </span>
          </Tooltip>
        ))}
    </Box>
  );
}

function StatusText({
  icon,
  color,
  children,
}: {
  icon?: ReactNode;
  color: string;
  children: ReactNode;
}) {
  return (
    <Typography
      variant="body2"
      color={color}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
    >
      {icon}
      <Tooltip title={children} describeChild>
        <Box
          component="span"
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {children}
        </Box>
      </Tooltip>
    </Typography>
  );
}
